import React, { useMemo, useState, useEffect } from "react";
import { Modal, Button, Form } from "react-bootstrap";

interface Props {
  show: boolean;
  onClose: () => void;
  onSave: (payload: {
    amount: number;
    paymentMode: PaymentMode;
    transactionNo?: string;
    bankName?: string;
    cardType?: string;
  }) => void;

  invoiceNo: string;
  patientName: string;
  totalBilled: number;
  previouslyPaid: number;
}

type PaymentMode = "CASH" | "UPI" | "CARD" | "ONLINE";

const INR = new Intl.NumberFormat("en-IN", {
  style: "currency",
  currency: "INR",
});

export default function CollectPaymentModal({
  show,
  onClose,
  onSave,
  invoiceNo,
  patientName,
  totalBilled,
  previouslyPaid,
}: Props) {
  const [paymentMode, setPaymentMode] = useState<PaymentMode>("CASH");
  const [amount, setAmount] = useState<number>(0);
  const [transactionNo, setTransactionNo] = useState("");
  const [bankName, setBankName] = useState("");
  const [cardType, setCardType] = useState("");

  const outstanding = useMemo(
    () => totalBilled - previouslyPaid,
    [totalBilled, previouslyPaid],
  );

  const remainingBalance = useMemo(
    () => outstanding - amount,
    [outstanding, amount],
  );

  // Auto-fill full outstanding when modal opens
  useEffect(() => {
    if (show) {
      setAmount(outstanding);
      setPaymentMode("CASH");
      setTransactionNo("");
      setBankName("");
      setCardType("");
    }
  }, [show, outstanding]);

  const handleSave = () => {
    if (amount <= 0 || amount > outstanding) return;

    if (paymentMode !== "CASH" && !transactionNo.trim()) return;

    if (paymentMode === "CARD" && (!bankName.trim() || !cardType)) return;

    onSave({
      amount,
      paymentMode,
      transactionNo: transactionNo || undefined,
      bankName: bankName || undefined,
      cardType: cardType || undefined,
    });
  };

  return (
    <Modal show={show} onHide={onClose} centered backdrop="static">
      <Modal.Header closeButton className="border-0 pb-0">
        <Modal.Title className="fw-semibold">Collect Payment</Modal.Title>
      </Modal.Header>
      <Modal.Body className="p-4">
        {/* Invoice Details */}
        <div className="mb-3">
          <div className="d-flex justify-content-between mb-2">
            <span className="text-muted">Invoice</span>
            <span className="fw-medium">#{invoiceNo}</span>
          </div>

          <div className="d-flex justify-content-between mb-2">
            <span className="text-muted">Patient</span>
            <span className="fw-medium">{patientName}</span>
          </div>

          <div className="d-flex justify-content-between mb-2">
            <span className="text-muted">Total Billed</span>
            <span>{INR.format(totalBilled)}</span>
          </div>

          <div className="d-flex justify-content-between mb-2">
            <span className="text-muted">Previously Paid</span>
            <span>{INR.format(previouslyPaid)}</span>
          </div>

          <div className="d-flex justify-content-between fw-semibold">
            <span>Outstanding</span>
            <span className="text-danger">{INR.format(outstanding)}</span>
          </div>
        </div>

        <hr />

        {/* Payment Mode */}
        <div className="mb-3">
          <div className="text-uppercase small text-muted fw-semibold mb-2">
            Payment Mode
          </div>

          <div className="btn-group w-100 mb-3">
            {(["CASH", "UPI", "CARD", "ONLINE"] as PaymentMode[]).map((m) => (
              <button
                key={m}
                type="button"
                className={`btn ${
                  paymentMode === m ? "btn-dark" : "btn-outline-secondary"
                }`}
                onClick={() => setPaymentMode(m)}
              >
                {m}
              </button>
            ))}
          </div>

          <Form.Label className="small text-muted fw-semibold">
            Amount Receiving Now
          </Form.Label>
          <Form.Control
            type="number"
            value={amount}
            min={0}
            max={outstanding}
            onChange={(e) => setAmount(Number(e.target.value || 0))}
          />

          {paymentMode !== "CASH" && (
            <>
              <Form.Label className="small text-muted fw-semibold mt-3">
                Transaction Number
              </Form.Label>
              <Form.Control
                type="text"
                value={transactionNo}
                onChange={(e) => setTransactionNo(e.target.value)}
              />
            </>
          )}

          {paymentMode === "CARD" && (
            <>
              <Form.Label className="small text-muted fw-semibold mt-3">
                Bank Name
              </Form.Label>
              <Form.Control
                type="text"
                value={bankName}
                onChange={(e) => setBankName(e.target.value)}
              />

              <Form.Label className="small text-muted fw-semibold mt-3">
                Card Type
              </Form.Label>
              <Form.Select
                value={cardType}
                onChange={(e) => setCardType(e.target.value)}
              >
                <option value="">Select</option>
                <option value="DEBIT">Debit</option>
                <option value="CREDIT">Credit</option>
              </Form.Select>
            </>
          )}
        </div>

        <hr />

        <div className="d-flex justify-content-between fw-semibold">
          <span>Remaining Balance</span>
          <span className={remainingBalance > 0 ? "text-danger" : ""}>
            {INR.format(remainingBalance)}
          </span>
        </div>

        <div className="d-flex justify-content-between align-items-center mt-4">
          <Button
            variant="link"
            className="text-muted text-decoration-none p-0"
            onClick={onClose}
          >
            Cancel
          </Button>

          <Button
            variant="success"
            onClick={handleSave}
            disabled={amount <= 0 || amount > outstanding}
          >
            Save Payment
          </Button>
        </div>
      </Modal.Body>
    </Modal>
  );
}
